<template>
  <div class="rpt-page">
    <div class="rpt-head">
      <div>
        <div class="rpt-crumb">Reportes</div>
        <h2 class="rpt-title">Vencimientos y certificación de lotes</h2>
        <p class="rpt-subtitle">
          Riesgo regulatorio: lotes vencidos o próximos a vencer, lotes sin
          certificado y productos con registro sanitario vencido. El vencimiento
          se calcula por fecha, no por el estado guardado del lote.
        </p>
      </div>
      <div class="rpt-actions">
        <v-btn
          icon
          to="/reportes/guia"
          title="Cómo leer este reporte"
          class="rpt-help"
        >
          <v-icon>help_outline</v-icon>
        </v-btn>
        <v-btn
          class="rpt-btn--ghost"
          depressed
          :disabled="!detalle.length"
          @click="exportar"
        >
          <v-icon left small>file_download</v-icon>Exportar
        </v-btn>
        <v-btn class="rpt-btn" depressed :loading="loading" @click="load">
          <v-icon left small>refresh</v-icon>Actualizar
        </v-btn>
      </div>
    </div>

    <v-layout row wrap class="rpt-filters">
      <v-flex md4 sm6 xs12>
        <v-select
          :items="laboratorioItems"
          v-model="labId"
          label="Laboratorio"
          prepend-icon="business"
          clearable
        ></v-select>
      </v-flex>
      <v-flex md4 sm6 xs12>
        <v-select
          :items="horizonteOptions"
          v-model="horizonteDias"
          label="Horizonte de alerta"
          prepend-icon="event"
        ></v-select>
      </v-flex>
      <v-flex md4 sm6 xs12>
        <v-select
          :items="limitOptions"
          v-model="limit"
          label="Filas de detalle"
          prepend-icon="list"
        ></v-select>
      </v-flex>
    </v-layout>

    <!-- El desfase entre fecha y estado es el hallazgo operativo del reporte:
         si el sistema no marca los lotes vencidos, nadie los ve venir. -->
    <v-alert
      v-if="resumen && resumen.estadoDesfasado > 0"
      :value="true"
      color="#E6C57D"
      icon="warning"
      class="rpt-alert"
    >
      <strong>{{ resumen.estadoDesfasado }}</strong> lotes ya vencieron según su
      fecha pero siguen sin marcarse como vencidos en el sistema. Este reporte
      usa la fecha real; el estado guardado del lote no es confiable hasta que
      se corrija el proceso que lo actualiza.
    </v-alert>

    <v-layout row wrap v-if="resumen">
      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile rpt-tile--risk">
          <span class="rpt-tile__label">Lotes vencidos</span>
          <span class="rpt-tile__value">{{ resumen.vencidos }}</span>
          <span class="rpt-tile__foot">
            {{ resumen.pctVencidos === null ? '—' : resumen.pctVencidos }}% de
            {{ resumen.total }} lotes activos
          </span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">
            Vencen en {{ horizonteDias }} días
          </span>
          <span class="rpt-tile__value">
            {{ resumen.porVencer }}<small>lotes</small>
          </span>
          <span class="rpt-tile__foot">ventana de acción preventiva</span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Sin certificado</span>
          <span class="rpt-tile__value">
            {{ resumen.sinCertificado }}<small>lotes</small>
          </span>
          <span class="rpt-tile__foot">certificación pendiente</span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Registro sanitario vencido</span>
          <span class="rpt-tile__value">
            {{ resumen.registroVencido }}<small>lotes</small>
          </span>
          <span class="rpt-tile__foot">
            el producto perdió vigencia de registro
          </span>
        </div>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex lg5 sm12 xs12 v-if="chartData">
        <v-widget content-bg="white" title="Lotes por ventana de vencimiento">
          <div slot="widget-content">
            <e-chart
              :path-option="chartOptions"
              width="90%"
              height="320px"
            ></e-chart>
          </div>
        </v-widget>
      </v-flex>

      <v-flex lg7 sm12 xs12>
        <v-widget content-bg="white" title="Concentración por laboratorio">
          <div slot="widget-content">
            <div class="rpt-scroll">
              <table class="rpt-table">
                <thead>
                  <tr>
                    <th>Laboratorio</th>
                    <th class="num">Lotes</th>
                    <th class="num">Vencidos</th>
                    <th class="num">Por vencer</th>
                    <th class="num">Sin cert.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(l, i) in porLaboratorio" :key="i">
                    <td>{{ l.laboratorio }}</td>
                    <td class="num rpt-muted">{{ l.n }}</td>
                    <td class="num">
                      <span
                        :class="[
                          'rpt-pill',
                          l.vencidos ? 'rpt-pill--warn' : '',
                        ]"
                      >
                        {{ l.vencidos }}
                      </span>
                    </td>
                    <td class="num">
                      <span
                        :class="[
                          'rpt-pill',
                          l.porVencer ? 'rpt-pill--soft' : '',
                        ]"
                      >
                        {{ l.porVencer }}
                      </span>
                    </td>
                    <td class="num rpt-muted">{{ l.sinCertificado }}</td>
                  </tr>
                  <tr v-if="!porLaboratorio.length">
                    <td colspan="5" class="rpt-empty">
                      Ningún laboratorio con lotes vencidos o por vencer.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex lg12 sm12 xs12>
        <v-widget
          content-bg="white"
          :title="
            `Detalle: vencidos y con vencimiento dentro de ${horizonteDias} días`
          "
        >
          <div slot="widget-content">
            <div class="rpt-scroll">
              <table class="rpt-table">
                <thead>
                  <tr>
                    <th>Lote</th>
                    <th>Producto</th>
                    <th>Laboratorio</th>
                    <th># Registro</th>
                    <th class="num">Vence</th>
                    <th class="num">Días</th>
                    <th class="num">Certificado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="b in detalle" :key="b.id">
                    <td class="rpt-strong">{{ b.batchNumber }}</td>
                    <td>{{ b.producto }}</td>
                    <td class="rpt-muted">{{ b.laboratorio || '—' }}</td>
                    <td class="rpt-muted">{{ b.registroSanitario || '—' }}</td>
                    <td class="num rpt-muted">{{ date(b.expirationDate) }}</td>
                    <td class="num">
                      <span :class="['rpt-pill', pillVence(b.diasParaVencer)]">
                        {{ diasLabel(b.diasParaVencer) }}
                      </span>
                    </td>
                    <td class="num">
                      <span
                        :class="[
                          'rpt-pill',
                          b.certificate ? 'rpt-pill--ok' : 'rpt-pill--warn',
                        ]"
                      >
                        {{ b.certificate ? 'Sí' : 'No' }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!detalle.length">
                    <td colspan="7" class="rpt-empty">
                      Ningún lote vence dentro del horizonte seleccionado.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p
              v-if="meta && meta.truncado"
              class="rpt-muted"
              style="font-size:12px;margin:10px 2px 0"
            >
              Se muestran los {{ meta.limite }} lotes con vencimiento más
              próximo. Suba el límite de filas para ver más.
            </p>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import EChart from '@/components/chart/echart'
import VWidget from '@/components/VWidget'
import exportReport from '@/helpers/ReportExport'
import ReportFilters from './mixins/ReportFilters'

// Del riesgo consumado (terracota) al horizonte lejano (azul claro): el color
// baja de intensidad conforme se aleja la fecha.
const BUCKET_COLORS = {
  Vencido: '#BD614D',
  '0-30 días': '#D89A6A',
  '31-90 días': '#E6C57D',
  '91-180 días': '#8FB6E0',
  'Más de 180': '#254F87',
  'Sin fecha': '#94A3B8',
}

export default {
  components: { VWidget, EChart },

  mixins: [ReportFilters],

  data() {
    return {
      horizonteDias: 90,
      horizonteOptions: [30, 60, 90, 180, 365],
      limit: 100,
      limitOptions: [50, 100, 250, 500],
      meta: null,
      resumen: null,
      aging: [],
      porLaboratorio: [],
      detalle: [],
      chartData: null,
      loading: false,
    }
  },

  computed: {
    chartOptions() {
      return [
        ['dataset.source', this.chartData],
        ['legend.show', false],
        ['grid.left', 90],
        ['grid.right', 32],
        ['grid.top', 16],
        ['grid.bottom', 32],
        ['tooltip.trigger', 'axis'],
        ['tooltip.axisPointer.type', 'shadow'],
        // Barras horizontales: las etiquetas de tramo son largas y así se leen
        // sin rotar el texto.
        ['xAxis.type', 'value'],
        ['xAxis.name', 'lotes'],
        ['xAxis.nameTextStyle.color', '#64748b'],
        // El wrapper oculta las etiquetas de eje por defecto: hay que pedirlas.
        ['xAxis.axisLabel.show', true],
        ['xAxis.axisLine.show', false],
        ['xAxis.axisTick.show', false],
        ['xAxis.splitLine.lineStyle.color', '#eef2f7'],
        ['yAxis.type', 'category'],
        ['yAxis.inverse', true],
        ['yAxis.axisLabel.show', true],
        ['yAxis.axisTick.show', false],
        ['yAxis.axisLine.lineStyle.color', '#e2e8f0'],
        ['series[0].type', 'bar'],
        ['series[0].barMaxWidth', 24],
        ['series[0].itemStyle.barBorderRadius', [0, 4, 4, 0]],
        [
          'series[0].itemStyle.color',
          params => BUCKET_COLORS[params.name] || '#254F87',
        ],
      ]
    },
  },

  watch: {
    labId() {
      this.load()
    },
    horizonteDias() {
      this.load()
    },
    limit() {
      this.load()
    },
  },

  created() {
    this.loadCatalogos()
    this.load()
  },

  methods: {
    // Los días vienen negativos cuando el lote ya venció; se leen mejor como
    // "N atrás" que como un número negativo.
    diasLabel(dias) {
      if (dias === null || dias === undefined) return '—'
      return dias < 0 ? `${-dias} atrás` : dias
    },

    pillVence(dias) {
      return dias < 0 ? 'rpt-pill--warn' : 'rpt-pill--soft'
    },

    load() {
      this.chartData = null
      this.loading = true

      this.$store
        .dispatch(
          'reports/getVencimientos',
          this.params({ horizonteDias: this.horizonteDias, limit: this.limit })
        )
        .then(response => {
          const data = response && response.data
          if (!data) return
          this.meta = data.meta
          this.resumen = data.resumen
          this.aging = data.aging || []
          this.porLaboratorio = data.porLaboratorio || []
          this.detalle = data.detalle || []
          this.chartData = this.aging.map(row => ({
            tramo: row.bucket,
            Lotes: row.n,
          }))
        })
        .finally(() => {
          this.loading = false
        })
    },

    exportar() {
      exportReport('vencimientos-lotes', [
        {
          name: 'Detalle',
          rows: this.detalle,
          columns: [
            { key: 'batchNumber', label: 'Lote' },
            { key: 'producto', label: 'Producto' },
            { key: 'presentacion', label: 'Presentación' },
            { key: 'laboratorio', label: 'Laboratorio' },
            { key: 'registroSanitario', label: 'Registro sanitario' },
            {
              key: 'registroVence',
              label: 'Registro vence',
              format: this.date,
            },
            { key: 'expirationDate', label: 'Vence', format: this.date },
            { key: 'diasParaVencer', label: 'Días para vencer' },
            {
              key: 'certificate',
              label: 'Certificado',
              format: v => (v ? 'Sí' : 'No'),
            },
            { key: 'batchStatus', label: 'Estado en sistema' },
          ],
        },
        {
          name: 'Por laboratorio',
          rows: this.porLaboratorio,
          columns: [
            { key: 'laboratorio', label: 'Laboratorio' },
            { key: 'n', label: 'Lotes' },
            { key: 'vencidos', label: 'Vencidos' },
            { key: 'porVencer', label: 'Por vencer' },
            { key: 'sinCertificado', label: 'Sin certificado' },
          ],
        },
        {
          name: 'Ventanas',
          rows: this.aging,
          columns: [
            { key: 'bucket', label: 'Ventana' },
            { key: 'n', label: 'Lotes' },
            { key: 'sinCertificado', label: 'Sin certificado' },
          ],
        },
      ])
    },
  },
}
</script>
